import { Badge, DataTable, ErrorState, PageHeader, StatTile, type ColumnDef } from '@zuruflex/ui';
import type { BadgeComparison } from '@zuruflex/types';
import { useQuery } from '@tanstack/react-query';
import { BadgeCheck, FlaskConical, ShoppingCart, Car } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { useTableLabels } from '../lib/table-labels.js';

/**
 * How each listing badge is performing, against the cars that earned it and
 * were deliberately shown without it.
 *
 * The holdout is what makes this page worth having. Comparing badged cars to
 * unbadged ones would only rediscover that the rules select good cars —
 * every figure here compares two sets of *equally eligible* cars, one shown
 * its badge and one not.
 *
 * Nothing on this page comes from tracking anybody. The counts are a daily
 * rollup of how many cars carried a badge and how many bookings landed on
 * them, which is why the site still sets nothing but a language and a theme.
 */
export function BadgesPage() {
  const { t, i18n } = useTranslation('admin');
  const tableLabels = useTableLabels();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['badge-stats'],
    queryFn: () => api.badgeStats(),
  });

  const rows = useMemo(() => data ?? [], [data]);

  const totals = useMemo(() => {
    const bookings = rows.reduce((sum, row) => sum + row.shown.bookings + row.heldOut.bookings, 0);
    const shownBookings = rows.reduce((sum, row) => sum + row.shown.bookings, 0);
    const smallestArm = rows.reduce(
      (min, row) => Math.min(min, row.heldOut.bookings),
      rows.length > 0 ? Number.POSITIVE_INFINITY : 0,
    );
    const ready = rows.filter((row) => row.sufficient).length;
    return { bookings, shownBookings, smallestArm, ready };
  }, [rows]);

  const columns = useMemo<ColumnDef<BadgeComparison>[]>(
    () => [
      {
        accessorKey: 'badgeKey',
        header: t('badgeStats.column.badge'),
        cell: ({ row }) => <span className="font-medium text-text">{row.original.badgeKey}</span>,
      },
      {
        id: 'shownVehicles',
        header: t('badgeStats.column.shownCars'),
        cell: ({ row }) => <Figure value={row.original.shown.vehicleDays} />,
      },
      {
        id: 'heldVehicles',
        header: t('badgeStats.column.heldCars'),
        cell: ({ row }) => <Figure value={row.original.heldOut.vehicleDays} />,
      },
      {
        id: 'shownRate',
        header: t('badgeStats.column.shownRate'),
        cell: ({ row }) => <Rate value={row.original.shown.bookingsPerThousand} />,
      },
      {
        id: 'heldRate',
        header: t('badgeStats.column.heldRate'),
        cell: ({ row }) => <Rate value={row.original.heldOut.bookingsPerThousand} />,
      },
      {
        id: 'lift',
        header: t('badgeStats.column.lift'),
        /*
         * Never a bare point estimate. Below the sample gate this says so in
         * words, because a dashboard that renders a confident "+18.4%" off
         * nine bookings is worse than one that admits it does not know — the
         * number gets quoted either way.
         */
        cell: ({ row }) => {
          const { sufficient, liftPercent, confidenceLow, confidenceHigh, bookingsNeeded } =
            row.original;
          if (!sufficient || liftPercent === null) {
            return (
              <span className="text-text-subtle">
                {t('badgeStats.notEnough', { count: bookingsNeeded })}
              </span>
            );
          }
          return (
            <span className="tabular-nums">
              <Badge variant={liftPercent > 0 ? 'success' : 'default'}>
                {liftPercent > 0 ? '+' : ''}
                {liftPercent.toFixed(0)}%
              </Badge>{' '}
              <span className="text-text-muted">
                {t('badgeStats.range', {
                  low: confidenceLow!.toFixed(0),
                  high: confidenceHigh!.toFixed(0),
                })}
              </span>
            </span>
          );
        },
      },
    ],
    [t],
  );

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="space-y-6">
      <PageHeader title={t('badgeStats.title')} description={t('badgeStats.description')} />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label={t('badgeStats.live')}
          value={String(rows.length)}
          hint={t('badgeStats.liveHint', { count: totals.ready })}
          icon={<BadgeCheck className="size-4" />}
          isLoading={isLoading}
          accent
        />
        <StatTile
          label={t('badgeStats.eligible')}
          value={totals.bookings.toLocaleString(i18n.language)}
          hint={t('badgeStats.eligibleHint')}
          icon={<ShoppingCart className="size-4" />}
          isLoading={isLoading}
        />
        <StatTile
          label={t('badgeStats.shown')}
          value={totals.shownBookings.toLocaleString(i18n.language)}
          hint={t('badgeStats.shownHint')}
          icon={<Car className="size-4" />}
          isLoading={isLoading}
        />
        <StatTile
          label={t('badgeStats.smallest')}
          value={Number.isFinite(totals.smallestArm) ? String(totals.smallestArm) : '—'}
          hint={t('badgeStats.smallestHint')}
          icon={<FlaskConical className="size-4" />}
          isLoading={isLoading}
        />
      </div>

      <DataTable
        labels={tableLabels}
        columns={columns}
        data={rows}
        isLoading={isLoading}
        emptyMessage={t('badgeStats.empty')}
      />

      <p className="text-[13px] text-text-muted">{t('badgeStats.footnote')}</p>
    </div>
  );
}

function Figure({ value }: { value: number }) {
  return <span className="tabular-nums text-text-muted">{value.toLocaleString()}</span>;
}

function Rate({ value }: { value: number | null }) {
  if (value === null) return <span className="text-text-subtle">—</span>;
  return <span className="tabular-nums text-text">{value.toFixed(1)}</span>;
}
